import { useMemo, useState } from 'react'

import { resourceListTag, useOpenWindow, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  EMPTY_PRODUCT_FILTER,
  OWN_CATALOG_CAPABILITIES,
  OWN_GROUP_TAG,
  OWN_PRODUCT_TAG,
  OWN_WINDOWS,
  OwnProductsScreen,
  ownGroups,
  ownNomenclatureApi,
  ownProducts,
  productsArgs,
  productsVm,
  type OwnProductsActions,
  type ProductFilterDraft,
} from '@krp/resource-own-ui'

import { useAppDispatch } from '../../app/hooks'
import { useOwnCapability } from './useOwnCapability'

/**
 * Binding вкладки «Номенклатура» своей базы (RP-OWN3): rid — из адреса окна. Отбор, который человек набирает, и применённый —
 * намерение, пока открыт экран; страницы — курсоры службы (назад — по запомненным курсорам). Данные — в кэше запросов.
 */
export function OwnProductsBinding() {
  const window = useWindow()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const canWrite = useOwnCapability(resourceId, OWN_CATALOG_CAPABILITIES.catalogWrite)

  const [filter, setFilter] = useState<ProductFilterDraft>(EMPTY_PRODUCT_FILTER)
  const [applied, setApplied] = useState<ProductFilterDraft>(EMPTY_PRODUCT_FILTER)
  /** Курсоры открытых страниц: первая — `null`, последняя — текущая. */
  const [cursors, setCursors] = useState<readonly (string | null)[]>([null])
  const cursor = cursors[cursors.length - 1] ?? null

  const args = useMemo(() => (resourceId === '' ? null : productsArgs(resourceId, applied, cursor)), [applied, cursor, resourceId])
  const page = useQueryResult(ownProducts, args)
  const groupArgs = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const groups = useQueryResult(ownGroups, groupArgs)

  const vm = productsVm({ filter, applied, page, groups: groups.data ?? [], pageNumber: cursors.length, canWrite })

  const actions = useMemo<OwnProductsActions>(
    () => ({
      filterChanged: setFilter,
      apply: () => {
        setApplied(filter)
        setCursors([null])
      },
      reset: () => {
        setFilter(EMPTY_PRODUCT_FILTER)
        setApplied(EMPTY_PRODUCT_FILTER)
        setCursors([null])
      },
      next: () => {
        const next = page.data?.nextCursor
        if (next != null) setCursors((previous) => [...previous, next])
      },
      previous: () => setCursors((previous) => (previous.length > 1 ? previous.slice(0, -1) : previous)),
      reload: () =>
        dispatch(
          ownNomenclatureApi.util.invalidateTags([resourceListTag(OWN_PRODUCT_TAG, resourceId), resourceListTag(OWN_GROUP_TAG, resourceId)]),
        ),
      open: (productId) => openWindow(OWN_WINDOWS.product, { rid: resourceId, id: productId }),
      create: () => openWindow(OWN_WINDOWS.productCreate, { rid: resourceId }),
      openGroups: () => openWindow(OWN_WINDOWS.groups, { rid: resourceId }),
    }),
    [dispatch, filter, openWindow, page.data?.nextCursor, resourceId],
  )

  return <OwnProductsScreen vm={vm} actions={actions} />
}
